import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {EnvironmentService} from '../environment.service';
import {ClientService} from '../auth/client.service';

export type CmsContentType = 'SitePage' | 'LandingPage' | 'BlogPost';
export type TranslationJobStatus = 'Queued' | 'Running' | 'Completed' | 'Failed';
export type TranslationSyncMode = 'off' | 'include' | 'exclude';

export interface TranslationSyncConfig {
    mode: TranslationSyncMode;
    includeIds: number[];
    excludeIds: number[];
}

export interface TranslationSettings {
  clientId: string;
  defaultInstructions: string;
  defaultModel: string;
  formPolicy: string;
  ctaPolicy: string;
    autoTranslateLanguages?: string[];
    syncConnectionId?: string;
    syncSitePages?: TranslationSyncConfig;
    syncLandingPages?: TranslationSyncConfig;
    syncBlogPosts?: TranslationSyncConfig;
  updatedAt?: string;
}

export interface TranslationSyncRunRequest {
    contentType?: CmsContentType;
    pageId?: number;
    force?: boolean;
}

export interface TranslationSyncResult {
    pagesChecked: number;
    pagesQueued: number;
    jobsQueued: number;
}

export interface TranslationJobRequest {
  contentType: CmsContentType;
  sourceId: number;
  targetLanguage: string;
  sourceLanguage?: string;
  connectionId?: string;
  connectionName?: string;
  instructions?: string;
  model?: string;
}

export interface TranslationJobLogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | string;
  code: string;
  message: string;
  path?: string;
}

export interface TranslationJob {
  id: string;
  clientId: string;
  contentType: CmsContentType;
  sourceId: number;
  targetLanguage: string;
  sourceLanguage?: string;
  connectionId?: string;
  connectionName?: string;
  instructions?: string;
  model?: string;
  status: TranslationJobStatus;
  createdAt: string;
  updatedAt: string;
  targetId?: number;
  targetUrl?: string;
  targetUpdatedAt?: string;
  logs?: TranslationJobLogEntry[];
  error?: string;
}

export interface CmsPageSummary {
  id: number;
  name: string;
  language?: string;
  slug?: string;
  url?: string;
  state?: string;
  updatedAt?: string;
  masterId?: number;
  isMaster?: boolean;
  variations?: CmsLanguageVariation[];
  shineVariations?: CmsLanguageVariation[];
  outOfSyncVariations?: CmsLanguageVariation[];
}

export interface CmsLanguageVariation {
  id: number;
  language?: string;
  url?: string;
  state?: string;
  updatedAt?: string;
}

export interface ConnectionInfo {
  id: string;
  name?: string;
  providerRef?: string;
  type?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TranslationsApiService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(EnvironmentService);
  private readonly clientService = inject(ClientService);

  private withTenant(path: string): string {
    const clientId = (this.clientService.client?.id || '').trim();
    if (!clientId) return path;

    const alreadyScoped = /(?:^|[?&])(tenant_id|tenantId|clientId|client_id)=/i.test(path);
    if (alreadyScoped) return path;

    const delimiter = path.includes('?') ? '&' : '?';
    return `${path}${delimiter}client_id=${encodeURIComponent(clientId)}`;
  }

  private normalize(path: string): string {
    const base = (this.env.translationsBaseServerUrl || '').replace(/\/+$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${base}${this.withTenant(cleanPath)}`;
  }

  getSettings() {
    return this.http.get<TranslationSettings>(this.normalize('/api/v1/translations/settings'));
  }

  saveSettings(settings: TranslationSettings) {
    return this.http.put<TranslationSettings>(this.normalize('/api/v1/translations/settings'), settings);
  }

  listConnections() {
    return this.http.get<ConnectionInfo[]>(this.normalize('/api/v1/translations/connections'));
  }

  listPages(
    contentType: CmsContentType,
    options?: {
      connectionId?: string;
      connectionName?: string;
      after?: string;
      limit?: number;
      groupByMaster?: boolean;
    },
  ) {
    let params = new HttpParams().set('contentType', contentType);
    if (options?.connectionId) params = params.set('connectionId', options.connectionId);
    if (options?.connectionName) params = params.set('connectionName', options.connectionName);
    if (options?.after) params = params.set('after', options.after);
    if (options?.limit) params = params.set('limit', options.limit);
    if (options?.groupByMaster) params = params.set('groupByMaster', 'true');

    return this.http.get<{items: CmsPageSummary[]; next?: string}>(this.normalize('/api/v1/translations/pages'), {params});
  }

  listJobs(limit: number = 50, offset: number = 0) {
    const params = new HttpParams().set('limit', limit).set('offset', offset);
    return this.http.get<TranslationJob[]>(this.normalize('/api/v1/translations/jobs'), {params});
  }

  createJob(request: TranslationJobRequest) {
    return this.http.post<TranslationJob>(this.normalize('/api/v1/translations/jobs'), request);
  }

    runSync(request?: TranslationSyncRunRequest) {
        return this.http.post<TranslationSyncResult>(this.normalize('/api/v1/translations/sync/run'), request ?? {});
    }
}
